import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  ScrollArea,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ExternalLinkIcon, MoveRightIcon } from "lucide-react";
import type { ClassSessionDTO } from "../../domain/contracts/sessions";
import { useUpdateClassSession } from "../api/queries";
import { ClassSessionForm } from "./ClassSessionForm";

type EditSessionDialogProps = {
  session: ClassSessionDTO;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onOpenMove?: () => void;
};

export function EditSessionDialog({
  session,
  open,
  onOpenChange,
  onSuccess,
  onOpenMove,
}: EditSessionDialogProps) {
  const nav = useNavigate();
  const updateSession = useUpdateClassSession(session.id);

  const initialValues = {
    templateId: session.templateId,
    date: session.date ?? "",
    status: session.status,
    startTime: session.startTime?.slice(0, 5) ?? "09:00",
    endTime: session.endTime?.slice(0, 5) ?? "09:00",
    locationKind: session.locationKind,
    offSiteLabel: session.offSiteLabel ?? "",
    engagementKind: session.engagementKind,
    roomId: session.roomId ?? "",
    instructors: session.instructors,
    capacity: session.capacity ?? 20,
    notes: session.notes ?? "",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className="!max-w-[min(95vw,1200px)] max-h-[90vh] overflow-hidden p-0"
          showCloseButton
        >
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>ແກ້ໄຂ ຊົ່ວໂມງຮຽນ</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-4rem)] overflow-auto px-6 pb-6">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onOpenChange(false);
                    nav({
                      to: "/app/classes/sessions/$id",
                      params: { id: session.id },
                    });
                  }}
                >
                  <ExternalLinkIcon className="mr-2 size-4" />
                  ເບິ່ງລາຍລະອຽດ
                </Button>
                {onOpenMove != null && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      onOpenMove();
                    }}
                  >
                    <MoveRightIcon className="mr-2 size-4" />
                    ຍ້າຍຊົ່ວໂມງຮຽນ
                  </Button>
                )}
              </div>

              <ClassSessionForm
                key={open ? session.id : "closed"}
                initialValues={initialValues}
                isEdit
                sessionId={session.id}
                onSubmit={async (values) => {
                  try {
                    await updateSession.mutateAsync({
                      templateId: values.templateId,
                      date: values.date,
                      status: values.status,
                      startTime: values.startTime,
                      endTime: values.endTime,
                      locationKind: values.locationKind,
                      offSiteLabel:
                        values.locationKind === "off_site"
                          ? (values.offSiteLabel?.trim() || null)
                          : null,
                      engagementKind: values.engagementKind,
                      roomId:
                        values.locationKind === "off_site"
                          ? null
                          : values.roomId,
                      instructors: values.instructors,
                      capacity: values.capacity ?? 20,
                      notes: values.notes ?? null,
                      changeReason: values.changeReason || undefined,
                    });
                    onSuccess?.();
                    onOpenChange(false);
                  } catch {
                    // Error handled by mutation
                  }
                }}
                submitting={updateSession.isPending}
                onCancel={() => onOpenChange(false)}
              />
            </div>
          </ScrollArea>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
